/**
 * Step shape:
 *   selector   — CSS selector for the element to spotlight
 *   action     — 'highlight' (just narrate) or 'click' (click after dwell)
 *   title      — short title shown in the caption card
 *   narration  — body text shown in the caption card
 *   dwellMs    — how long to stay on this step before auto-advancing
 *   waitForMs  — max time to wait for the selector to appear before giving up
 *   padding    — optional spotlight padding override (px)
 *
 * The demo demonstrates the full flow including opening the Save modal, but
 * intentionally clicks "Cancel" instead of "Save Bid" — so the modal is shown
 * and the flow is narrated, but no real save is fired against the API.
 */
export const BID_DEMO_STEPS = [
  {
    selector: '[data-tour="bid-page-header"]',
    action:   'highlight',
    title:    'Welcome to the Bid Calculator',
    narration:
      "Pulse turns historical project data into defensible client bids. Let's build one in under a minute.",
    dwellMs:  4500,
  },
  {
    selector: '[data-tour="bid-tab-wizard"]',
    action:   'highlight',
    title:    'The Bid Wizard',
    narration:
      "We'll work in the wizard — a guided five-step flow that ends with a client-ready proposal.",
    dwellMs:  4000,
  },
  {
    selector: '[data-tour="step1-retainer-card"]',
    action:   'click',
    title:    'Step 1 · Engagement Type',
    narration:
      "First, pick how the engagement is structured. We'll go with an ongoing monthly retainer.",
    dwellMs:  3500,
  },
  {
    selector: '[data-tour="step1-retainer-a"]',
    action:   'click',
    title:    'Choose a Retainer Tier',
    narration:
      'Retainer A is full-service — media, social, and strategic oversight bundled into one monthly fee.',
    dwellMs:  3500,
  },
  {
    selector: '[data-tour="wizard-continue"]',
    action:   'click',
    title:    'Continue',
    narration:
      "Pulse advances us to the next step once it has what it needs.",
    dwellMs:  2500,
  },
  {
    selector: '[data-tour="step2-reference"]',
    action:   'click',
    title:    'Step 2 · Reference Past Work',
    narration:
      "Rather than starting cold, we'll base this bid on similar past engagements — accuracy, faster.",
    dwellMs:  4000,
  },
  {
    selector: '[data-tour="step3-engagement-table"]',
    action:   'highlight',
    title:    'Step 3 · Pick a Template',
    narration:
      'These are Won engagements pulled live from QuickBooks. Real hours, real costs, real margins.',
    dwellMs:  4500,
    waitForMs: 8000,
  },
  {
    selector: '[data-tour="step3-engagement-table"] tbody tr:first-child [data-tour="step3-use-template"]',
    action:   'click',
    title:    'Select a Reference',
    narration:
      "We'll pick the most similar past engagement and use it as our template. Pulse pre-fills services, roles, and hours from its actuals — and advances us to the build step.",
    dwellMs:  4000,
    waitForMs: 8000,
  },
  {
    selector: '[data-tour="step4-services"]',
    action:   'highlight',
    title:    'Step 4 · Build Services',
    narration:
      'Roles and hours are pre-populated from the reference. Edit anything inline — the price recalculates as you go.',
    dwellMs:  5000,
    waitForMs: 8000,
  },
  {
    selector: '[data-tour="step4-margin"]',
    action:   'highlight',
    title:    'Set Your Target Margin',
    narration:
      'Drag the slider to the margin you need. Bid value updates instantly so you can see the trade-off.',
    dwellMs:  4500,
  },
  {
    selector: '[data-tour="step4-summary"]',
    action:   'highlight',
    title:    'The Recommended Bid',
    narration:
      "Pulse shows the recommended price plus min and max bands — so you walk into the negotiation with a defensible range.",
    dwellMs:  5000,
  },
  {
    selector: '[data-tour="wizard-continue"]',
    action:   'click',
    title:    'On to the Proposal',
    narration:
      "With the bid dialed in, we advance to Step 5 — where Pulse turns these numbers into a client-ready proposal.",
    dwellMs:  3000,
    waitForMs: 6000,
  },
  {
    selector: '[data-tour="step5-header"]',
    action:   'highlight',
    title:    'Step 5 · Build Proposal',
    narration:
      "This is where the bid becomes a document — pick a template, tweak the language, preview the PDF, send it out.",
    dwellMs:  4500,
    waitForMs: 6000,
  },
  {
    selector: '[data-tour="step5-summary"]',
    action:   'highlight',
    title:    'Bid Carries Forward',
    narration:
      "Client, engagement, and the bid amount you just dialed in flow straight from Step 4 — no retyping, no copy-paste drift.",
    dwellMs:  4500,
  },
  {
    selector: '[data-tour="step5-template"]',
    action:   'highlight',
    title:    'Pick a Template',
    narration:
      "Choose from retainer, project, or short-form variants. Each is fully editable — the dropdown just sets the starting point.",
    dwellMs:  4500,
  },
  {
    selector: '[data-tour="step5-actions"]',
    action:   'highlight',
    title:    'Preview & Download',
    narration:
      "One click to preview the rendered PDF, another to download it. The proposal is ready to send.",
    dwellMs:  4500,
  },
  {
    selector: '[data-tour="wizard-save"]',
    action:   'click',
    title:    'Save the Bid',
    narration:
      "Before sending, hit Save Bid to lock the numbers and the proposal under Saved Bids.",
    dwellMs:  3500,
    waitForMs: 6000,
  },
  {
    selector: '[data-tour="save-modal"]',
    action:   'highlight',
    title:    'Name & Save',
    narration:
      "Give the bid a name, add a note for context, and Pulse keeps it under Saved Bids — ready to load, tweak, or share.",
    dwellMs:  5000,
    waitForMs: 4000,
  },
  {
    selector: '[data-tour="save-modal-cancel"]',
    action:   'click',
    title:    'Demo · No Real Save',
    narration:
      "Since this is a walkthrough, we'll close the dialog instead of saving. In the real flow, this is where your bid gets persisted.",
    dwellMs:  4000,
  },
  {
    selector: '[data-tour="bid-page-header"]',
    action:   'highlight',
    title:    "Bid to Proposal, in a Minute",
    narration:
      "From a blank page to a defensible bid and a client-ready proposal — all backed by real engagement data. Build it once, save it, send it, win the work.",
    dwellMs:  6000,
  },
];

export default BID_DEMO_STEPS;
